import { useState } from 'react';
import { useTheme } from '../context/ThemeContext';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

type ContactMethod = 'mail' | 'telefon';
type SubmitStatus = 'idle' | 'loading' | 'success' | 'error';

export default function QuickQuestion() {
  const { theme } = useTheme();
  const { ref, isVisible } = useScrollAnimation({ threshold: 0.15 });

  const [contactMethod, setContactMethod] = useState<ContactMethod>('mail');
  const [question, setQuestion] = useState('');
  const [contactValue, setContactValue] = useState('');
  const [status, setStatus] = useState<SubmitStatus>('idle');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!question.trim() || !contactValue.trim()) return;
    setStatus('loading');

    const params = new URLSearchParams();
    params.append('frage', question);
    params.append('antwort_via', contactMethod);
    if (contactMethod === 'mail') {
      params.append('email', contactValue);
    } else {
      params.append('telefon', contactValue);
    }

    try {
      const res = await fetch('https://readdy.ai/api/form/d6td910q778icnc8un30', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });
      if (res.ok) {
        setStatus('success');
        setQuestion('');
        setContactValue('');
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section
      className="py-12 px-6"
      style={{ fontFamily: theme.fonts.body, backgroundColor: '#0D0D0D' }}
    >
      <div
        ref={ref}
        className="max-w-7xl mx-auto"
        style={{
          opacity: isVisible ? 1 : 0,
          transform: isVisible ? 'translateY(0)' : 'translateY(16px)',
          transition: 'opacity 0.6s ease, transform 0.6s ease',
        }}
      >
        {status === 'success' ? (
          <div className="flex items-center gap-4">
            <h2
              className="text-xl font-bold whitespace-nowrap shrink-0"
              style={{
                fontFamily: theme.fonts.heading,
                background: `linear-gradient(135deg, ${theme.colors.secondary} 0%, ${theme.colors.accent} 100%)`,
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Oder noch Fragen?
            </h2>
            <div className="flex items-center gap-2 ml-4">
              <div
                className="w-6 h-6 rounded-full flex items-center justify-center shrink-0"
                style={{ background: `linear-gradient(135deg, ${theme.colors.secondary} 0%, ${theme.colors.accent} 100%)` }}
              >
                <i className="ri-check-line text-white text-xs"></i>
              </div>
              <p className="text-sm font-medium text-white/70">
                Frage erhalten – ich melde mich schnell!
              </p>
            </div>
          </div>
        ) : (
          <form
            data-readdy-form
            id="quick-question-form"
            onSubmit={handleSubmit}
            className="flex flex-wrap items-center gap-x-6 gap-y-4"
          >
            {/* Heading */}
            <h2
              className="text-xl font-bold whitespace-nowrap shrink-0"
              style={{
                fontFamily: theme.fonts.heading,
                color: '#ffffff',
              }}
            >
              Oder noch Fragen?
            </h2>

            {/* Divider */}
            <div className="hidden lg:block w-px h-6 shrink-0 bg-white/15" />

            {/* Question input */}
            <input
              type="text"
              name="frage"
              required
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="Ihre Frage…"
              className="flex-1 min-w-[180px] bg-transparent border-b-2 outline-none py-1.5 text-sm transition-colors duration-200"
              style={{
                color: '#fff',
                borderColor: question ? theme.colors.secondary : 'rgba(255,255,255,0.25)',
              }}
            />

            {/* Divider */}
            <div className="hidden lg:block w-px h-6 shrink-0 bg-white/15" />

            {/* Antwort via toggle */}
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-xs font-semibold whitespace-nowrap text-white/50">
                Antwort via
              </span>
              <div className="inline-flex rounded-full p-0.5" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
                {(['mail', 'telefon'] as ContactMethod[]).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => { setContactMethod(m); setContactValue(''); }}
                    className="px-4 py-1 rounded-full text-xs font-semibold transition-all duration-200 whitespace-nowrap cursor-pointer"
                    style={
                      contactMethod === m
                        ? {
                            background: `linear-gradient(135deg, ${theme.colors.secondary} 0%, ${theme.colors.accent} 100%)`,
                            color: '#fff',
                          }
                        : { color: 'rgba(255,255,255,0.5)', background: 'transparent' }
                    }
                  >
                    {m === 'mail' ? 'E-Mail' : 'Telefon'}
                  </button>
                ))}
              </div>
            </div>

            {/* Contact input */}
            {contactMethod === 'mail' ? (
              <input
                key="email"
                type="email"
                name="email"
                required
                value={contactValue}
                onChange={(e) => setContactValue(e.target.value)}
                placeholder="ihre@email.de"
                className="flex-1 min-w-[160px] bg-transparent border-b-2 outline-none py-1.5 text-sm transition-colors duration-200"
                style={{
                  color: '#fff',
                  borderColor: contactValue ? theme.colors.secondary : 'rgba(255,255,255,0.25)',
                }}
              />
            ) : (
              <input
                key="telefon"
                type="tel"
                name="telefon"
                required
                value={contactValue}
                onChange={(e) => setContactValue(e.target.value)}
                placeholder="+49 160 …"
                className="flex-1 min-w-[160px] bg-transparent border-b-2 outline-none py-1.5 text-sm transition-colors duration-200"
                style={{
                  color: '#fff',
                  borderColor: contactValue ? theme.colors.secondary : 'rgba(255,255,255,0.25)',
                }}
              />
            )}

            {/* Submit */}
            <button
              type="submit"
              disabled={status === 'loading'}
              className="shrink-0 px-6 py-2.5 rounded-lg text-sm font-bold transition-all duration-200 hover:scale-105 whitespace-nowrap cursor-pointer disabled:opacity-60"
              style={{
                background: `linear-gradient(135deg, ${theme.colors.secondary} 0%, ${theme.colors.accent} 100%)`,
                color: '#fff',
              }}
            >
              {status === 'loading' ? (
                <span className="flex items-center gap-1.5">
                  <i className="ri-loader-4-line animate-spin"></i>
                  Sende…
                </span>
              ) : (
                'Abschicken'
              )}
            </button>

            {status === 'error' && (
              <p className="text-xs w-full text-red-400">
                Etwas hat nicht geklappt – bitte nochmal versuchen.
              </p>
            )}
          </form>
        )}
      </div>
    </section>
  );
}
